import React, { useContext, useEffect, useState, useRef } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import "./navbar.css";
import { AuthContext } from "../../Context/auth";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Badge, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";

import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BuildIcon from "@mui/icons-material/Build";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CakeIcon from "@mui/icons-material/Cake";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

import { collection, query, where, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../../Config/firebase";
import PesquisaPendenteAviso from "../../KPIs/PesquisaPendenteAviso";
import { getUsuarioAtualComTenant, tenantWhere } from "../../Utils/tenant";
import LogoRelatorio from "../LogoRelatorio/LogoRelatorio";
import { LOGO_NAVEGACAO } from "../../Config/branding";

function normalizarData(valor) {
  if (!valor) return null;

  if (valor?.toDate) {
    const data = valor.toDate();
    return Number.isNaN(data?.getTime?.()) ? null : data;
  }

  if (valor instanceof Date) {
    return Number.isNaN(valor.getTime()) ? null : valor;
  }

  if (typeof valor === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) {
      const [ano, mes, dia] = valor.split("-").map(Number);
      return new Date(ano, mes - 1, dia);
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(valor)) {
      const [dia, mes, ano] = valor.split("/").map(Number);
      return new Date(ano, mes - 1, dia);
    }

    const data = new Date(valor);
    return Number.isNaN(data.getTime()) ? null : data;
  }

  return null;
}

function obterQuantidadeDiasAte(dataLimite) {
  if (!(dataLimite instanceof Date) || Number.isNaN(dataLimite.getTime())) {
    return null;
  }

  const hoje = new Date();
  const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const inicioLimite = new Date(
    dataLimite.getFullYear(),
    dataLimite.getMonth(),
    dataLimite.getDate()
  );

  return Math.ceil((inicioLimite.getTime() - inicioHoje.getTime()) / 86400000);
}

function adicionarDias(data, quantidadeDias) {
  if (!(data instanceof Date) || Number.isNaN(data.getTime())) return null;

  const resultado = new Date(data);
  resultado.setDate(resultado.getDate() + quantidadeDias);
  return resultado;
}

function calcularVencimentoCalibracaoPrensa(dataCalibracao, validade) {
  const dataBase = normalizarData(dataCalibracao);
  const textoValidade = String(validade || "").trim().toLowerCase().replace(",", ".");

  if (!dataBase || !textoValidade) {
    return null;
  }

  const numeroValidade = Number(textoValidade.match(/[\d.]+/)?.[0]);

  if (!Number.isFinite(numeroValidade) || numeroValidade <= 0) {
    return null;
  }

  const vencimento = new Date(dataBase);

  if (textoValidade.includes("mes")) {
    vencimento.setMonth(vencimento.getMonth() + numeroValidade);
    return vencimento;
  }

  vencimento.setFullYear(vencimento.getFullYear() + numeroValidade);
  return vencimento;
}

function formatarDataVencimento(data) {
  if (!(data instanceof Date) || Number.isNaN(data.getTime())) {
    return "Data não informada";
  }

  return new Intl.DateTimeFormat("pt-BR").format(data);
}

function montarStatusVencimento(diasRestantes) {
  if (diasRestantes === null) return "";
  if (diasRestantes === 0) return "Vence hoje";
  if (diasRestantes === 1) return "Vence em 1 dia";
  if (diasRestantes > 1) return `Vence em ${diasRestantes} dias`;
  if (diasRestantes === -1) return "Vencido há 1 dia";
  return `Vencido há ${Math.abs(diasRestantes)} dias`;
}

function montarDescricaoPrensa(dados = {}) {
  const partes = [
    dados.codigo && String(dados.codigo).trim(),
    dados.empresa && String(dados.empresa).trim(),
    dados.serie && `Série ${String(dados.serie).trim()}`
  ].filter(Boolean);

  if (partes.length > 0) {
    return partes.join(" - ");
  }

  return "Calibração de prensa";
}

function equipamentoAtivoParaCalibracao(dados = {}) {
  const status = String(dados.status || "").trim().toLowerCase();
  if (["inativo", "desativado", "inactive"].includes(status)) return false;

  if (dados.ativo === true || dados.ativo === 1) return true;

  const ativo = String(dados.ativo ?? "").trim().toLowerCase();
  return ativo === "true" || ativo === "1" || ativo === "ativo";
}

function obterProximoAniversario(dataNascimento) {
  const nascimento = normalizarData(dataNascimento);
  if (!nascimento) return null;

  const agora = new Date();
  const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  let aniversario = new Date(
    hoje.getFullYear(),
    nascimento.getMonth(),
    nascimento.getDate()
  );

  if (aniversario < hoje) {
    aniversario = new Date(
      hoje.getFullYear() + 1,
      nascimento.getMonth(),
      nascimento.getDate()
    );
  }

  return {
    data: aniversario,
    diasRestantes: Math.round((aniversario.getTime() - hoje.getTime()) / 86400000)
  };
}

function descricaoAniversario(diasRestantes) {
  if (diasRestantes === 0) return "Hoje";
  if (diasRestantes === 1) return "Amanhã";
  return `Em ${diasRestantes} dias`;
}

function Navbar() {
  const { setLogado, permissoesAcesso = {} } = useContext(AuthContext);
  const navbarRef = useRef(null);
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
 
  const [openCadastrar, setOpenCadastrar] = useState(false);
  
  const [temSolicitacoesPendentes, setTemSolicitacoesPendentes] = useState(false);

  const [usuario, setUsuario] = useState(null);
  const [openPesquisa, setOpenPesquisa] = useState(false);
  const [openOcorrencias, setOpenOcorrencias] = useState(false);
  const [kpis, setKpis] = useState([]);

  const [aprovacoesPendentes, setAprovacoesPendentes] = useState([]);
  const [equipVencidos, setEquipVencidos] = useState([]);
  const [equipAVencer, setEquipAVencer] = useState([]);
  const [prensasVencidas, setPrensasVencidas] = useState([]);
  const [prensasAVencer, setPrensasAVencer] = useState([]);
  const [locacoesAVencer, setLocacoesAVencer] = useState([]);
  const [medicoesVencendo, setMedicoesVencendo] = useState([]);
  const [aniversariantes, setAniversariantes] = useState([]);
  const [saldoDinheiroCaixinha, setSaldoDinheiroCaixinha] = useState(null);
  const [limiteAlertaCaixinha, setLimiteAlertaCaixinha] = useState(200);
  const [caixinhaCarregada, setCaixinhaCarregada] = useState(false);
  const [aniversarianteLogado, setAniversarianteLogado] = useState(null);
  const [openParabens, setOpenParabens] = useState(false);
  const [openAlertaCaixinha, setOpenAlertaCaixinha] = useState(false);
  const [openNotificacoes, setOpenNotificacoes] = useState(false);


  // ðŸ”‘ CONTROLE DO REGISTRO DE EVENTO
  const [abrindoEvento, setAbrindoEvento] = useState(false);

  const location = useLocation();
  const history = useHistory();
  const auth = getAuth();

  const isDashboard = location.pathname === "/dashboard";

  const tipoUsuario =
    (localStorage.getItem("tipoUsuario") || "").toLowerCase();
  const isSuperadmin = localStorage.getItem("superadmin") === "S";
  const isAdminPrincipal = localStorage.getItem("adminPrincipal") === "S";
  const podeAcessarMedicoes =
    tipoUsuario === "admin" &&
    (isAdminPrincipal || permissoesAcesso.medicoes === true);
  const podeAcessarLocacoes =
    tipoUsuario === "admin" &&
    (isAdminPrincipal || permissoesAcesso.locacaoEquipamentos === true);
  const chaveAlertaCaixinha = `alerta-caixinha-verificado:${localStorage.getItem("tenantId") || "geral"}`;
  const tipoUsuarioLabel = isSuperadmin
    ? "Superadmin"
    : tipoUsuario
      ? tipoUsuario.charAt(0).toUpperCase() + tipoUsuario.slice(1)
      : "Não informado";

  /* =========================
     USUÁRIO LOGADO
  ========================= */
          useEffect(() => {
            if (!tipoUsuario) return;

            const unsubscribe = onAuthStateChanged(auth, user => {
              setUsuario(user || null);
            });

            return () => unsubscribe();
          }, [auth, tipoUsuario]);

  useEffect(() => {
    if (!usuario?.uid || isSuperadmin) {
      setAniversariantes([]);
      setAniversarianteLogado(null);
      return undefined;
    }

    let unsubscribe = () => {};
    let cancelado = false;

    async function ouvirAniversariantes() {
      const usuarioTenant = await getUsuarioAtualComTenant();
      if (cancelado) return;

      unsubscribe = onSnapshot(
        query(
          collection(db, "dp_funcionarios"),
          ...tenantWhere(usuarioTenant)
        ),
        snapshot => {
          const lista = snapshot.docs
            .map(documento => {
              const dados = documento.data();
              const proximoAniversario = obterProximoAniversario(dados.dataNascimento);

              if (
                dados.ativo === false ||
                dados.tipo !== "funcionario" ||
                !proximoAniversario ||
                proximoAniversario.diasRestantes < 0 ||
                proximoAniversario.diasRestantes > 2
              ) {
                return null;
              }

              return {
                id: documento.id,
                ...dados,
                aniversario: proximoAniversario.data,
                diasRestantes: proximoAniversario.diasRestantes
              };
            })
            .filter(Boolean)
            .sort((a, b) => a.diasRestantes - b.diasRestantes || String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));

          setAniversariantes(lista);

          const aniversarianteAtual = lista.find(
            item => item.diasRestantes === 0 && item.usuarioId === usuario.uid
          ) || null;

          setAniversarianteLogado(aniversarianteAtual);

          if (aniversarianteAtual) {
            const hoje = new Date();
            const dataAtual = [
              hoje.getFullYear(),
              String(hoje.getMonth() + 1).padStart(2, "0"),
              String(hoje.getDate()).padStart(2, "0")
            ].join("-");
            const chaveFelicitacao = `felicitacao-aniversario:${usuario.uid}:${dataAtual}`;

            if (sessionStorage.getItem(chaveFelicitacao) !== "S") {
              sessionStorage.setItem(chaveFelicitacao, "S");
              setOpenParabens(true);
            }
          }
        },
        erro => {
          console.error("Erro ao carregar aniversariantes:", erro);
          setAniversariantes([]);
          setAniversarianteLogado(null);
        }
      );
    }

    ouvirAniversariantes().catch(erro => {
      console.error("Erro ao iniciar notificações de aniversários:", erro);
      setAniversariantes([]);
      setAniversarianteLogado(null);
    });

    return () => {
      cancelado = true;
      unsubscribe();
    };
  }, [usuario, isSuperadmin]);

useEffect(() => {
  if (isSuperadmin || (tipoUsuario !== "admin" && tipoUsuario !== "colaborador")) {
    setTemSolicitacoesPendentes(false);
    return;
  }

  let unsubscribe = () => {};
  let cancelado = false;

  async function ouvirSolicitacoes() {
    const usuarioTenant = await getUsuarioAtualComTenant();
    if (cancelado) return;

  const q = query(
    collection(db, "tblSolicitacoesServicos"),
    ...tenantWhere(usuarioTenant)
  );

  unsubscribe = onSnapshot(
    q,
    (snap) => {
      const temPendente = snap.docs.some((docSnap) => {
        const dados = docSnap.data();

        if (dados.status === "pendente") return true;

        return (dados.ensaios || []).some(
          (ensaio) => (ensaio.status || "pendente") === "pendente"
        );
      });

      setTemSolicitacoesPendentes(temPendente);
    },
    (error) => {
      console.error("ERRO AO OUVIR O.S. INTERNA:", error);
      setTemSolicitacoesPendentes(false);
    }
  );
  }

  ouvirSolicitacoes().catch((error) => {
    console.error("ERRO AO OUVIR O.S. INTERNA:", error);
    setTemSolicitacoesPendentes(false);
  });

  return () => {
    cancelado = true;
    unsubscribe();
  };
}, [tipoUsuario]);


 /* =========================
     FIRESTORE APROVACOES
  ========================= */
  useEffect(() => {
  if (isSuperadmin || tipoUsuario !== "admin") return;

  async function carregarAprovacoes() {
    const usuarioTenant = await getUsuarioAtualComTenant();
    const q = query(
      collection(db, "aprovacoes"),
      ...tenantWhere(usuarioTenant),
      where("status", "==", "pendente")
    );

    const snap = await getDocs(q);
    setAprovacoesPendentes(
      snap.docs.map(d => ({ id: d.id, ...d.data() }))
    );
  }

  carregarAprovacoes().catch(error => {
    console.error("ERRO AO OUVIR APROVAÇÕES:", error);
    setAprovacoesPendentes([]);
  });
}, [tipoUsuario, isSuperadmin]);


 /* =========================
     FIRESTORE EQUIPAMENTOS VENCIDOS / A VENCER
  ========================= */

  useEffect(() => {
    if (isSuperadmin || (tipoUsuario !== "admin" && tipoUsuario !== "qualidade")) return;

    let equipamentosSnapAtual = null;
    let documentosSnapAtual = null;
    let tiposDocumentosSnapAtual = null;

    function atualizarNotificacoesEquipamentos() {
      if (
        !equipamentosSnapAtual ||
        !documentosSnapAtual ||
        !tiposDocumentosSnapAtual
      ) {
        return;
      }

      const documentosPorEquipamento = {};

      documentosSnapAtual.forEach(documento => {
        const dadosDocumento = documento.data();
        const equipamentoId = dadosDocumento.equipamentoId;

        if (!equipamentoId) return;
        if (!documentosPorEquipamento[equipamentoId]) {
          documentosPorEquipamento[equipamentoId] = [];
        }

        documentosPorEquipamento[equipamentoId].push({
          id: documento.id,
          ...dadosDocumento
        });
      });

      const tiposDocumentos = tiposDocumentosSnapAtual.docs.map(documento => ({
        id: documento.id,
        ...documento.data()
      }));

      const vencidos = [];
      const aVencer = [];

      equipamentosSnapAtual.forEach(equipamento => {
        const dadosEquipamento = equipamento.data();

        if (!equipamentoAtivoParaCalibracao(dadosEquipamento)) return;
        if (!dadosEquipamento.calibravel) return;
        if (dadosEquipamento.tipoUso === "locacao") return;

        const tiposObrigatorios = tiposDocumentos.filter(tipo => {
          if (!tipo.obrigatorio || !tipo.temVencimento) return false;

          const escopo = String(tipo.escopo || "").trim().toLowerCase();
          if (!escopo || escopo === "geral") return true;

          return (
            escopo === "especifico" &&
            tipo.equipamentoId === equipamento.id
          );
        });

        const documentosEquipamento =
          documentosPorEquipamento[equipamento.id] || [];

        tiposObrigatorios.forEach(tipo => {
          const codigoTipo = String(tipo.codigo || "").trim().toUpperCase();
          const documento = documentosEquipamento.find(item => {
            const codigoDocumento = String(
              item.tipoDocumentoCodigo || ""
            ).trim().toUpperCase();
            const statusDocumento = String(
              item.statusDocumento || ""
            ).trim().toUpperCase();

            return (
              codigoDocumento === codigoTipo &&
              statusDocumento === "ENVIADO"
            );
          });

          if (!documento) return;

          const vencimento = normalizarData(documento.dataVencimento);
          if (!vencimento) return;

          const diasRestantes = obterQuantidadeDiasAte(vencimento);
          if (diasRestantes === null || diasRestantes > 45) return;

          const registro = {
            id: equipamento.id,
            notificacaoId: documento.id,
            nome: dadosEquipamento.nome || "Equipamento",
            tipoDocumento: tipo.nome || documento.tipoDocumentoNome || "",
            vencimento,
            diasRestantes
          };

          if (diasRestantes < 0) {
            vencidos.push(registro);
          } else {
            aVencer.push(registro);
          }
        });
      });

      const ordenarPorVencimento = (a, b) =>
        a.vencimento.getTime() - b.vencimento.getTime();

      const removerDuplicados = (lista) => Array.from(
        new Map(
          lista.map((item) => [`${item.id}-${item.notificacaoId}`, item])
        ).values()
      );

      setEquipVencidos(removerDuplicados(vencidos).sort(ordenarPorVencimento));
      setEquipAVencer(removerDuplicados(aVencer).sort(ordenarPorVencimento));
    }

    function tratarErroNotificacoes(erro) {
      console.error("Erro ao carregar notificações de equipamentos:", erro);
      setEquipVencidos([]);
      setEquipAVencer([]);
    }

    let unsubscribeEquipamentos = () => {};
    let unsubscribeDocumentos = () => {};
    let unsubscribeTiposDocumentos = () => {};
    let cancelado = false;

    async function iniciarNotificacoesEquipamentos() {
      const usuarioTenant = await getUsuarioAtualComTenant();
      if (cancelado) return;

      const qEquipamentos = query(
        collection(db, "equipamentos"),
        ...tenantWhere(usuarioTenant)
      );
      const qDocumentos = query(
        collection(db, "eq_documentos"),
        ...tenantWhere(usuarioTenant)
      );
      const qTiposDocumentos = query(
        collection(db, "eq_tipos_documentos"),
        ...tenantWhere(usuarioTenant)
      );

    unsubscribeEquipamentos = onSnapshot(
      qEquipamentos,
      snap => {
        equipamentosSnapAtual = snap;
        atualizarNotificacoesEquipamentos();
      },
      tratarErroNotificacoes
    );

    unsubscribeDocumentos = onSnapshot(
      qDocumentos,
      snap => {
        documentosSnapAtual = snap;
        atualizarNotificacoesEquipamentos();
      },
      tratarErroNotificacoes
    );

    unsubscribeTiposDocumentos = onSnapshot(
      qTiposDocumentos,
      snap => {
        tiposDocumentosSnapAtual = snap;
        atualizarNotificacoesEquipamentos();
      },
      tratarErroNotificacoes
    );
    }

    iniciarNotificacoesEquipamentos().catch(tratarErroNotificacoes);

    return () => {
      cancelado = true;
      unsubscribeEquipamentos();
      unsubscribeDocumentos();
      unsubscribeTiposDocumentos();
    };
  }, [tipoUsuario]);

  useEffect(() => {
    if (isSuperadmin || (tipoUsuario !== "admin" && tipoUsuario !== "qualidade")) return;

    async function carregarCalibracoesPrensa() {
      const usuarioTenant = await getUsuarioAtualComTenant();
      const snap = await getDocs(
        query(
          collection(db, "tblCalibracaoPrensas"),
          ...tenantWhere(usuarioTenant)
        )
      );

      const vencidas = [];
      const aVencer = [];

      snap.forEach(item => {
        const dados = item.data();
        if (dados.ativo === false) return;

        const vencimento =
          normalizarData(dados.dataVencimento) ||
          calcularVencimentoCalibracaoPrensa(
            dados.dataCalibracao || dados.data,
            dados.validade
          );

        if (!vencimento) return;

        const diasRestantes = obterQuantidadeDiasAte(vencimento);

        if (diasRestantes === null) return;

        const registro = {
          id: item.id,
          nome: montarDescricaoPrensa(dados),
          codigo: dados.codigo || "",
          vencimento,
          diasRestantes
        };

        if (diasRestantes < 0) {
          vencidas.push(registro);
        } else if (diasRestantes <= 30) {
          aVencer.push(registro);
        }
      });

      setPrensasVencidas(vencidas);
      setPrensasAVencer(aVencer);
    }

    carregarCalibracoesPrensa().catch(error => {
      console.error("Erro ao carregar calibrações de prensa:", error);
      setPrensasVencidas([]);
      setPrensasAVencer([]);
    });

    const atualizarCalibracoesPrensa = (evento) => {
      if (evento?.detail?.ativo === false && evento.detail.id) {
        setPrensasVencidas((atuais) =>
          atuais.filter((item) => item.id !== evento.detail.id)
        );
        setPrensasAVencer((atuais) =>
          atuais.filter((item) => item.id !== evento.detail.id)
        );
      }

      carregarCalibracoesPrensa().catch(error => {
        console.error("Erro ao atualizar calibrações de prensa:", error);
      });
    };
    window.addEventListener(
      "calibracoes-prensa-atualizadas",
      atualizarCalibracoesPrensa
    );

    return () => {
      window.removeEventListener(
        "calibracoes-prensa-atualizadas",
        atualizarCalibracoesPrensa
      );
    };
  }, [tipoUsuario]);

  useEffect(() => {
    if (isSuperadmin || !usuario?.uid || !podeAcessarLocacoes) {
      setLocacoesAVencer([]);
      return;
    }

    let unsubscribeLocacoes = () => {};
    let cancelado = false;

    async function iniciarNotificacoesLocacoes() {
      const usuarioTenant = await getUsuarioAtualComTenant();
      if (cancelado) return;

      const qLocacoes = query(
        collection(db, "tblLocacoesEquipamentos"),
        ...tenantWhere(usuarioTenant)
      );

      unsubscribeLocacoes = onSnapshot(qLocacoes, (snap) => {
        const aVencer = snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((locacao) => {
            if (locacao.status === "devolvida" || locacao.status === "cancelada") return false;

            const vencimento = normalizarData(locacao.dataFim);
            const diasRestantes = obterQuantidadeDiasAte(vencimento);
            return diasRestantes !== null && diasRestantes >= 0 && diasRestantes <= 5;
          })
          .map((locacao) => {
            const vencimento = normalizarData(locacao.dataFim);
            return {
              ...locacao,
              vencimento,
              diasRestantes: obterQuantidadeDiasAte(vencimento),
            };
          })
          .sort((a, b) => a.vencimento.getTime() - b.vencimento.getTime());

        setLocacoesAVencer(aVencer);
      }, (erro) => {
        console.error("Erro ao carregar notificações de locações:", erro);
        setLocacoesAVencer([]);
      });
    }

    iniciarNotificacoesLocacoes().catch((erro) => {
      console.error("Erro ao iniciar notificações de locações:", erro);
      setLocacoesAVencer([]);
    });

    return () => {
      cancelado = true;
      unsubscribeLocacoes();
    };
  }, [usuario?.uid, podeAcessarLocacoes, isSuperadmin]);

  useEffect(() => {
    if (isSuperadmin || !podeAcessarMedicoes) {
      setMedicoesVencendo([]);
      return undefined;
    }

    let unsubscribe = () => {};
    let cancelado = false;

    async function iniciarNotificacoesMedicoes() {
      const usuarioTenant = await getUsuarioAtualComTenant();
      if (cancelado) return;

      const qMedicoes = query(
        collection(db, "tblMedicoes"),
        ...tenantWhere(usuarioTenant)
      );

      unsubscribe = onSnapshot(qMedicoes, (snap) => {
        const medicoesMaisRecentes = new Map();

        snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .forEach((medicao) => {
            const dataAte = normalizarData(
              medicao.periodoOutrosServicosFim || medicao.dataFinal
            );
            if (!dataAte) return;

            const chave = medicao.obraId || `${medicao.clienteId || ""}:${medicao.obra || ""}`;
            const atual = medicoesMaisRecentes.get(chave);
            const dataAtual = normalizarData(
              atual?.periodoOutrosServicosFim || atual?.dataFinal
            );

            if (!atual || !dataAtual || dataAte > dataAtual) {
              medicoesMaisRecentes.set(chave, medicao);
            }
          });

        const vencendo = Array.from(medicoesMaisRecentes.values())
          .map((medicao) => {
            if (
              medicao.notificarAposDias === "" ||
              medicao.notificarAposDias === null ||
              medicao.notificarAposDias === undefined
            ) {
              return null;
            }

            const dias = Number(medicao.notificarAposDias);
            if (!Number.isInteger(dias) || dias < 0) return null;

            const vencimento = adicionarDias(
              normalizarData(
                medicao.periodoOutrosServicosFim || medicao.dataFinal
              ),
              dias
            );
            return {
              ...medicao,
              vencimento,
              diasRestantes: obterQuantidadeDiasAte(vencimento),
            };
          })
          .filter((medicao) =>
            medicao &&
            medicao.diasRestantes !== null &&
            medicao.diasRestantes <= 0
          )
          .sort((a, b) => a.vencimento.getTime() - b.vencimento.getTime());

        setMedicoesVencendo(vencendo);
      }, (erro) => {
        console.error("Erro ao carregar notificações de medições:", erro);
        setMedicoesVencendo([]);
      });
    }

    iniciarNotificacoesMedicoes().catch((erro) => {
      console.error("Erro ao iniciar notificações de medições:", erro);
      setMedicoesVencendo([]);
    });

    return () => {
      cancelado = true;
      unsubscribe();
    };
  }, [isSuperadmin, podeAcessarMedicoes]);

  useEffect(() => {
    if (isSuperadmin || !isAdminPrincipal) {
      setSaldoDinheiroCaixinha(null);
      setCaixinhaCarregada(false);
      return undefined;
    }

    let unsubscribe = () => {};
    let cancelado = false;
    setCaixinhaCarregada(false);

    async function ouvirSaldoCaixinha() {
      const usuarioTenant = await getUsuarioAtualComTenant();
      if (cancelado) return;

      const consulta = query(
        collection(db, "tblCaixinhaEmpresa"),
        ...tenantWhere(usuarioTenant)
      );

      unsubscribe = onSnapshot(
        consulta,
        (snapshot) => {
          const mesAtual = new Date().toISOString().slice(0, 7);
          const registros = snapshot.docs.map((documento) => documento.data());
          const configuracao = registros.find(
            (registro) => registro.tipo === "configuracao" && registro.configuracaoCaixinha === true
          );
          const limiteConfigurado = Number(configuracao?.limiteSaldoDinheiro);
          const lancamentos = registros.filter(
            (registro) => registro.tipo === "entrada" || registro.tipo === "saida"
          );

          setLimiteAlertaCaixinha(
            Number.isFinite(limiteConfigurado) && limiteConfigurado >= 0
              ? limiteConfigurado
              : 200
          );

          if (lancamentos.length === 0) {
            setSaldoDinheiroCaixinha(null);
            setCaixinhaCarregada(true);
            return;
          }

          const calcularSaldoDinheiro = (registros) => registros.reduce((total, lancamento) => {
            if (lancamento.carteira === "cartao") return total;

            const valor = Number(lancamento.valor || 0);
            return lancamento.tipo === "entrada" ? total + valor : total - valor;
          }, 0);
          const lancamentosDoMes = lancamentos.filter((lancamento) =>
            String(lancamento.data || "").startsWith(mesAtual)
          );
          const temSaldoAnterior = lancamentosDoMes.some(
            (lancamento) =>
              lancamento.tipo === "entrada" &&
              lancamento.tipoEntrada === "saldo_anterior" &&
              lancamento.carteira !== "cartao"
          );

          let saldo = calcularSaldoDinheiro(lancamentosDoMes);

          if (!temSaldoAnterior && mesAtual >= "2026-09") {
            const [ano, mes] = mesAtual.split("-").map(Number);
            const dataMesAnterior = new Date(ano, mes - 2, 1);
            const mesBase = `${dataMesAnterior.getFullYear()}-${String(
              dataMesAnterior.getMonth() + 1
            ).padStart(2, "0")}`;
            const lancamentosBase = lancamentos.filter((lancamento) =>
              mesAtual === "2026-09"
                ? String(lancamento.data || "") < `${mesAtual}-01`
                : String(lancamento.data || "").startsWith(mesBase)
            );
            const saldoAnterior = calcularSaldoDinheiro(lancamentosBase);

            if (saldoAnterior > 0) saldo += saldoAnterior;
          }

          setSaldoDinheiroCaixinha(saldo);
          setCaixinhaCarregada(true);
        },
        (erro) => {
          console.error("Erro ao carregar saldo do caixinha:", erro);
          setSaldoDinheiroCaixinha(null);
          setCaixinhaCarregada(true);
        }
      );
    }

    ouvirSaldoCaixinha().catch((erro) => {
      console.error("Erro ao iniciar notificação do caixinha:", erro);
      setSaldoDinheiroCaixinha(null);
      setCaixinhaCarregada(true);
    });

    return () => {
      cancelado = true;
      unsubscribe();
    };
  }, [isAdminPrincipal, isSuperadmin]);

  const caixinhaAbaixoDoMinimo =
    saldoDinheiroCaixinha !== null && saldoDinheiroCaixinha < limiteAlertaCaixinha;

  useEffect(() => {
    if (!caixinhaCarregada || !isAdminPrincipal) {
      setOpenAlertaCaixinha(false);
      return;
    }

    if (sessionStorage.getItem(chaveAlertaCaixinha) === "S") {
      setOpenAlertaCaixinha(false);
      return;
    }

    if (caixinhaAbaixoDoMinimo) {
      setOpenAlertaCaixinha(true);
      return;
    }

    sessionStorage.setItem(chaveAlertaCaixinha, "S");
    setOpenAlertaCaixinha(false);
  }, [caixinhaAbaixoDoMinimo, caixinhaCarregada, chaveAlertaCaixinha, isAdminPrincipal]);

  function fecharAlertaCaixinha() {
    sessionStorage.setItem(chaveAlertaCaixinha, "S");
    setOpenAlertaCaixinha(false);
  }
      const medicoesParaHoje = medicoesVencendo.filter(
        (medicao) => medicao.diasRestantes === 0
      );
      const medicoesAtrasadas = medicoesVencendo.filter(
        (medicao) => medicao.diasRestantes < 0
      );

      const temErro =
            (tipoUsuario === "admin" && aprovacoesPendentes.length > 0) ||
            ((tipoUsuario === "admin" || tipoUsuario === "qualidade") &&
              (equipVencidos.length > 0 || prensasVencidas.length > 0)) ||
            caixinhaAbaixoDoMinimo ||
            medicoesAtrasadas.length > 0;

          const temAviso =
            !temErro &&
            (((tipoUsuario === "admin" || tipoUsuario === "qualidade") &&
               (equipAVencer.length > 0 || prensasAVencer.length > 0 || locacoesAVencer.length > 0)) ||
              aniversariantes.length > 0 ||
              medicoesParaHoje.length > 0);



  /* =========================
     KPIs POR ESCOPO (PESQUISAS)
  ========================= */
  useEffect(() => {
  async function carregarKPIs() {
    if (isSuperadmin || !tipoUsuario) return;

    // ðŸ”‘ ADMIN HERDA KPIs DE COLABORADOR
    const escopoConsulta =
      tipoUsuario === "admin" ? "colaborador" : tipoUsuario;

    const escoposPermitidos =
  tipoUsuario === "admin"
    ? ["colaborador", "cliente_colaborador"]
    : [tipoUsuario, "cliente_colaborador"];

const usuarioTenant = await getUsuarioAtualComTenant();

const q = query(
  collection(db, "kpis"),
  ...tenantWhere(usuarioTenant),
  where("ativo", "==", true),
  where("tipoKPI", "==", "PESQUISA"),
  where("escopo", "in", escoposPermitidos)
);

    const snap = await getDocs(q);
    setKpis(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }

  carregarKPIs().catch(error => {
    console.error("Erro ao carregar KPIs de pesquisa:", error);
    setKpis([]);
  });
}, [tipoUsuario]);


          useEffect(() => {
          function handleClickOutside(event) {
            if (
              navbarRef.current &&
              !navbarRef.current.contains(event.target)
            ) {
              setOpenPesquisa(false);
              setOpenOcorrencias(false);
              setOpenNotificacoes(false); 
              setOpenCadastrar(false);
            }
          }


  document.addEventListener("mousedown", handleClickOutside);

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);


  /* =========================
     PÁGINA PRINCIPAL
  ========================= */
  function irParaPaginaPrincipal() {
    if (isSuperadmin) return history.push("/app/cadastrousuarios");
    if (tipoUsuario === "admin") return history.push("/dashboard");
    if (tipoUsuario === "colaborador")
      return history.push("/dashboard");
    if (tipoUsuario === "cliente") return history.push("/app/laudos");
    history.push("/app");
  }

  /* =========================
     REGISTRAR OCORRENCIA / NC
  ========================= */
  async function irParaRegistroEvento() {
    if (abrindoEvento) return;

    try {
      setAbrindoEvento(true);

      const q = query(
        collection(db, "kpis"),
        ...tenantWhere(await getUsuarioAtualComTenant()),
        where("ativo", "==", true),
        where("tipoKPI", "==", "EVENTO")
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        history.push("/kpi/evento/indisponivel");
        return;
      }

      const kpiEvento = snap.docs[0];
      history.push(`/kpi/evento/${kpiEvento.id}`);
    } catch (e) {
      console.error("Erro ao abrir registro de evento", e);
    } finally {
      setAbrindoEvento(false);
    }
  }

  /* =========================
     SAIR
  ========================= */
  function Logout(e) {
  e.preventDefault();
  setOpenLogoutModal(true); // ðŸ”¥ abre o popup
}
function confirmarLogout() {
  setOpenLogoutModal(false);
  setLogado(false);
  sessionStorage.clear();
  localStorage.clear();
  history.push("/");
}

  const acessoTarget = window.location.hostname === "concretoengenharia-sgq.web.app" &&
    usuario && tipoUsuario !== "cliente" &&
    (isSuperadmin || tipoUsuario === "admin" || tipoUsuario === "colaborador") ? (
    <li className="nav-item">
      <a
        className="nav-link"
        href="https://www.normas.com.br/login"
        target="_blank"
        rel="noopener noreferrer"
        title="Acessar Normas ABNT em nova aba"
      >
        Normas ABNT
      </a>
    </li>
  ) : null;

  if (isSuperadmin) {
    return (
      <nav ref={navbarRef} className="navbar navbar-expand-md navbar-default">
        <div className="container-fluid">
          <div className="navbar-brand">
            <LogoRelatorio
              logoPadrao={LOGO_NAVEGACAO}
              monocromaticaBranca
              alt="Logo"
              style={{ height: 32, maxHeight: 32 }}
            />
          </div>

          <div className="collapse navbar-collapse show">
            <ul className="navbar-nav me-auto align-items-center">
              <li className="nav-item">
                <button
                  type="button"
                  className="nav-link btn btn-link"
                  onClick={irParaPaginaPrincipal}
                >
                  Clientes SaaS
                </button>
              </li>
              {acessoTarget}
            </ul>

            <ul className="navbar-nav ms-auto align-items-center">
              <li className="nav-item">
                <button
                  type="button"
                  className="nav-link btn btn-link"
                  onClick={(event) => {
                    event.preventDefault();
                    confirmarLogout();
                  }}
                >
                  Sair
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    );
  }


  return (
    <>
    <PesquisaPendenteAviso tipoUsuario={tipoUsuario} />
    <nav
            ref={navbarRef}
            className={`navbar navbar-expand-md ${
              isDashboard ? "navbar-dashboard" : "navbar-default"
            }`}
          >

      <div className="container-fluid">
        {!isDashboard && (
          <div className="navbar-brand">
  <LogoRelatorio
    logoPadrao={LOGO_NAVEGACAO}
    monocromaticaBranca
    alt="Logo"
    style={{ height: 32, maxHeight: 32 }}
  />
</div>

        )}

        <button
          className="navbar-toggler ms-auto"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarSupportedContent"
        >
          <span className="navbar-toggler-icon"></span>
        </button>

        <div className="collapse navbar-collapse" id="navbarSupportedContent">
          <ul className="navbar-nav me-auto align-items-center">
            <li className="nav-item">
              <button
                type="button"
                className="nav-link btn btn-link"
                onClick={irParaPaginaPrincipal}
              >
                Página Principal
              </button>
            </li>

            {acessoTarget}

            {(tipoUsuario === "admin" || tipoUsuario === "colaborador") && (
  <li className="nav-item">
    <Link
      to="/app/solicitacao-servicos"
      className="nav-link d-flex align-items-center gap-1"
      onClick={() => {
        setOpenCadastrar(false);
        setOpenPesquisa(false);
        setOpenOcorrencias(false);
        setOpenNotificacoes(false);
      }}
    >
      {temSolicitacoesPendentes ? (
        <Badge color="error" variant="dot">
          <NotificationsActiveIcon fontSize="small" />
        </Badge>
      ) : (
        <PlaylistAddCheckIcon fontSize="small" />
      )}

      O.S. interna
    </Link>
  </li>
)}

{(tipoUsuario === "admin" || tipoUsuario === "colaborador") && (
  <li className="nav-item position-relative">
    <button
      type="button"
      className="nav-link btn btn-link d-flex align-items-center gap-1"
      onClick={() => {
        setOpenCadastrar(c => !c);
        setOpenPesquisa(false);
        setOpenOcorrencias(false);
        setOpenNotificacoes(false);
      }}
    >
      Cadastrar
      {openCadastrar ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </button>

    {openCadastrar && (
      <ul className="dropdown-menu show mt-1 position-absolute">
        <li>
          <button
            className="dropdown-item"
            onClick={() => {
              setOpenCadastrar(false);
              history.push("/app/clientes");
            }}
          >
            Cadastrar Cliente
          </button>
        </li>

        <li>
          <button
            className="dropdown-item"
            onClick={() => {
              setOpenCadastrar(false);
              history.push("/app/cadastroobras");
            }}
          >
            Cadastrar Obra
          </button>
        </li>

        <li>
          <button
            className="dropdown-item"
            onClick={() => {
              setOpenCadastrar(false);
              history.push("/app/cadastro-concreteiras");
            }}
          >
            Cadastrar Concreteira
          </button>
        </li>

        {(tipoUsuario === "admin" || tipoUsuario === "colaborador") && (
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setOpenCadastrar(false);
                history.push("/app/qualificacao-fornecedores");
              }}
            >
              Qualificação de Fornecedores
            </button>
          </li>
        )}

        {(tipoUsuario === "admin" || tipoUsuario === "qualidade") && (
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setOpenCadastrar(false);
                history.push("/app/plano-comunicacao");
              }}
            >
              Plano de Comunicação
            </button>
          </li>
        )}

        {(tipoUsuario === "admin" || tipoUsuario === "qualidade") && (
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setOpenCadastrar(false);
                history.push("/app/programa-treinamentos");
              }}
            >
              Programa de Treinamentos
            </button>
          </li>
        )}

        {(tipoUsuario === "admin" || tipoUsuario === "qualidade") && (
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setOpenCadastrar(false);
                history.push("/app/listas-presenca-treinamentos");
              }}
            >
              Lista de Presença em Treinamentos
            </button>
          </li>
        )}

        {(tipoUsuario === "admin" || tipoUsuario === "qualidade") && (
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setOpenCadastrar(false);
                history.push("/app/quadro-competencias");
              }}
            >
              Quadro de Competências
            </button>
          </li>
        )}

        {(tipoUsuario === "admin" || tipoUsuario === "qualidade") && (
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setOpenCadastrar(false);
                history.push("/app/verificacao-competencias");
              }}
            >
              Verificação de Competências e Habilidades
            </button>
          </li>
        )}

        {(tipoUsuario === "admin" || tipoUsuario === "qualidade") && (
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setOpenCadastrar(false);
                history.push("/app/controle-manutencao-equipamentos");
              }}
            >
              Controle de Manutenção de Equipamentos
            </button>
          </li>
        )}

        {isAdminPrincipal && (
          <li>
            <button
              className="dropdown-item"
              onClick={() => {
                setOpenCadastrar(false);
                history.push("/app/configuracoes/logo-relatorios");
              }}
            >
              Logo do Sistema e Relatórios
            </button>
          </li>
        )}

        <li>
          <hr className="dropdown-divider" />
        </li>

        <li>
          <h6 className="dropdown-header">Ensaios</h6>
        </li>

        <li>
          <button
            className="dropdown-item"
            onClick={() => {
              setOpenCadastrar(false);
              history.push("/app/ensaio-raa");
            }}
          >
            Ensaio RAA
          </button>
        </li>

        <li>
          <button
            className="dropdown-item"
            onClick={() => {
              setOpenCadastrar(false);
              history.push("/app/ensaio-compactacao");
            }}
          >
            Ensaio Compactação
          </button>
        </li>

        <li>
          <button
            className="dropdown-item"
            onClick={() => {
              setOpenCadastrar(false);
              history.push("/app/cadastro-caracterizacao");
            }}
          >
            Ensaio Caracterização
          </button>
        </li>

        <li>
          <button
            className="dropdown-item"
            onClick={() => {
              setOpenCadastrar(false);
              history.push("/app/ensaio-argamassa");
            }}
          >
            Ensaio de argamassa - Tração e compressão
          </button>
        </li>

        <li>
          <button
            className="dropdown-item"
            onClick={() => {
              setOpenCadastrar(false);
              history.push("/app/cadastro-dosagem");
            }}
          >
            Dosagem
          </button>
        </li>
      </ul>
    )}
  </li>
)}


            {(tipoUsuario === "admin" || tipoUsuario === "colaborador") && (
                    <li className="nav-item">
                      <Link to="/sgq" className="nav-link">
                        SGQ
                      </Link>
                    </li>
                  )}

                  {(tipoUsuario === "admin" || tipoUsuario === "colaborador") && (
                    <li className="nav-item">
                      <Link to="/app/GerarEtiquetas" className="nav-link">
                        Etiquetas
                      </Link>
                    </li>
                  )}



{(tipoUsuario === "admin" || tipoUsuario === "colaborador") && (
  <li className="nav-item">
    <Link to="/app/rupturas-hoje" className="nav-link">
      Rupturas do Dia
    </Link>
  </li>
)}

{tipoUsuario === "colaborador" && (
  <li className="nav-item">
    <Link to="/app/controle-temperatura-tanque-cura" className="nav-link">
      Temp. Tanque Cura
    </Link>
  </li>
)}

            {/* PESQUISA */}
            <li className="nav-item position-relative">
              <button
                type="button"
                className="nav-link btn btn-link d-flex align-items-center gap-1"
                onClick={() => {
                  setOpenPesquisa(p => !p);
                  setOpenOcorrencias(false);
                }}
              >
                Pesquisa
                {openPesquisa ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </button>

              {openPesquisa && (
  <ul className="dropdown-menu show mt-1 position-absolute">
    {kpis
  .filter(kpi => kpi.tipoFormulario !== "PSICOSSOCIAL")
  .map(kpi => (
    <li key={kpi.id}>
      <button
        className="dropdown-item"
        onClick={() => {
          setOpenPesquisa(false);
          history.push(`/app/pesquisa/${kpi.id}`);
        }}
      >
        {kpi.nome}
      </button>
    </li>
  ))}
  </ul>
)}
            </li>

            {/* OCORRENCIAS / NC */}
            {(tipoUsuario === "admin" || tipoUsuario === "colaborador") && (
              <li className="nav-item position-relative">
                <button
                  type="button"
                  className="nav-link btn btn-link d-flex align-items-center gap-1"
                  onClick={() => {
                    setOpenOcorrencias(o => !o);
                    setOpenPesquisa(false);
                  }}
                >
                  Ocorrências
                  {openOcorrencias ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>

                {openOcorrencias && (
                          <ul className="dropdown-menu show mt-1 position-absolute">
                            {/* REGISTRAR – admin + colaborador */}
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => {
                                  setOpenOcorrencias(false);
                                  irParaRegistroEvento();
                                }}
                                disabled={abrindoEvento}
                              >
                                {abrindoEvento
                                  ? "Abrindo formulário..."
                                  : "Registrar Ocorrências"}
                              </button>
                            </li>

                            {/* RESPONDER – SOMENTE ADMIN */}
                            {tipoUsuario === "admin" && (
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => {
                                    setOpenOcorrencias(false);
                                    history.push("/app/ocorrencias");
                                  }}
                                >
                                  Responder Ocorrências
                                </button>
                              </li>
                            )}
                          </ul>
                        )}

              </li>
            )}
             <li className="nav-item">
              <button
                type="button"
                onClick={Logout}
                className="nav-link btn btn-link"
              >
                Sair
              </button>
            </li>

           {(tipoUsuario === "admin" || tipoUsuario === "qualidade" || aniversariantes.length > 0 || caixinhaAbaixoDoMinimo || medicoesVencendo.length > 0) && (
  <li className="nav-item position-relative me-3">
    <button
      type="button"
      className="btn btn-link nav-link position-relative"
      onClick={() => setOpenNotificacoes(o => !o)}
    >
      <NotificationsActiveIcon />

      {temErro && (
        <span
          style={{
            width: 8,
            height: 8,
            backgroundColor: "#d32f2f",
            borderRadius: "50%",
            position: "absolute",
            top: 6,
            right: 6
          }}
        />
      )}

      {temAviso && (
        <span
          style={{
            width: 8,
            height: 8,
            backgroundColor: "#f9a825",
            borderRadius: "50%",
            position: "absolute",
            top: 6,
            right: 6
          }}
        />
      )}
    </button>

    {openNotificacoes && (
      <div
        className="dropdown-menu show p-2 position-absolute end-0 notificacoes-menu"
        style={{ minWidth: 320 }}
      >
        {aprovacoesPendentes.length === 0 &&
          equipVencidos.length === 0 &&
          equipAVencer.length === 0 &&
          prensasVencidas.length === 0 &&
          prensasAVencer.length === 0 &&
           locacoesAVencer.length === 0 &&
          medicoesVencendo.length === 0 &&
          aniversariantes.length === 0 &&
          !caixinhaAbaixoDoMinimo && (
            <span className="dropdown-item-text text-muted">
              Nenhuma notificação
            </span>
          )}

        {caixinhaAbaixoDoMinimo && (
          <>
            <h6 className="dropdown-header text-danger">
              Saldo baixo no caixinha
            </h6>
            <button
              className="dropdown-item text-danger notificacao-vencimento"
              onClick={() => {
                setOpenNotificacoes(false);
                history.push("/app/caixinha-empresa");
              }}
            >
              <AccountBalanceWalletIcon fontSize="small" className="me-2" />
              <span className="notificacao-vencimento-conteudo">
                <span className="notificacao-vencimento-titulo">
                  Dinheiro disponível: {saldoDinheiroCaixinha.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL"
                  })}
                </span>
                <span className="notificacao-vencimento-detalhe">
                  O saldo está abaixo do limite de {limiteAlertaCaixinha.toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL"
                  })}.
                </span>
              </span>
            </button>
          </>
        )}

        {aniversariantes.length > 0 && (
          <>
            <h6 className="dropdown-header text-primary">
              Próximos aniversariantes
            </h6>

            {aniversariantes.map(item => (
              <div
                key={item.id}
                className="dropdown-item-text notificacao-vencimento"
              >
                <CakeIcon fontSize="small" className="me-2" />
                <span className="notificacao-vencimento-conteudo">
                  <span className="notificacao-vencimento-titulo">{item.nome}</span>
                  <span className="notificacao-vencimento-detalhe">
                    {descricaoAniversario(item.diasRestantes)} · {item.aniversario.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}
                  </span>
                </span>
              </div>
            ))}
          </>
        )}

        {tipoUsuario === "admin" && aprovacoesPendentes.length > 0 && (
          <>
            <h6 className="dropdown-header text-danger">
              Aprovações pendentes
            </h6>

            {aprovacoesPendentes.map(item => (
                  <button
                    key={item.id}
                    className="dropdown-item"
                    onClick={() => {
                      setOpenNotificacoes(false);
                      history.push("/app/aprovacoes");
                    }}
                  >
                <CheckCircleIcon fontSize="small" className="me-2" />
                {item.titulo || "Aprovação pendente"}
              </button>
            ))}
          </>
        )}

        {equipVencidos.length > 0 && (
          <>
            <h6 className="dropdown-header text-danger">
              Certificado vencido
            </h6>

            {equipVencidos.map(eq => (
              <button
                key={eq.notificacaoId}
                className="dropdown-item notificacao-vencimento"
                onClick={() => {
                  setOpenNotificacoes(false);
                  history.push(`/app/cadastro-equipamento/${eq.id}`);
                }}
              >
                <BuildIcon fontSize="small" className="me-2" />
                <span className="notificacao-vencimento-conteudo">
                  <span className="notificacao-vencimento-titulo">{eq.nome}</span>
                  <span className="notificacao-vencimento-detalhe">
                    Vencimento: {formatarDataVencimento(eq.vencimento)} ·{" "}
                    {montarStatusVencimento(eq.diasRestantes)}
                  </span>
                </span>
              </button>
            ))}
          </>
        )}

        {equipAVencer.length > 0 && (
          <>
            <h6 className="dropdown-header text-warning">
              Equipamentos a vencer
            </h6>

            {equipAVencer.map(eq => (
              <button
                key={eq.notificacaoId}
                className="dropdown-item notificacao-vencimento"
                onClick={() => {
                  setOpenNotificacoes(false);
                  history.push(`/app/cadastro-equipamento/${eq.id}`);
                }}
              >
                <BuildIcon fontSize="small" className="me-2" />
                <span className="notificacao-vencimento-conteudo">
                  <span className="notificacao-vencimento-titulo">{eq.nome}</span>
                  <span className="notificacao-vencimento-detalhe">
                    Vencimento: {formatarDataVencimento(eq.vencimento)} ·{" "}
                    {montarStatusVencimento(eq.diasRestantes)}
                  </span>
                </span>
              </button>
            ))}
          </>
        )}

        {prensasVencidas.length > 0 && (
          <>
            <h6 className="dropdown-header text-danger">
              Certificado de prensa vencido
            </h6>

            {prensasVencidas.map(prensa => (
              <button
                key={prensa.id}
                className="dropdown-item text-danger notificacao-vencimento"
                onClick={() => {
                  setOpenNotificacoes(false);
                  history.push(`/app/calibracao-prensa/${prensa.id}`);
                }}
              >
                <BuildIcon fontSize="small" className="me-2" />
                <span className="notificacao-vencimento-conteudo">
                  <span className="notificacao-vencimento-titulo">{prensa.nome}</span>
                  <span className="notificacao-vencimento-detalhe">
                    Vencimento: {formatarDataVencimento(prensa.vencimento)} ·{" "}
                    {montarStatusVencimento(prensa.diasRestantes)}
                  </span>
                </span>
              </button>
            ))}
          </>
        )}

        {prensasAVencer.length > 0 && (
          <>
            <h6 className="dropdown-header text-warning">
              Certificado de prensa a vencer
            </h6>

            {prensasAVencer.map(prensa => (
              <button
                key={prensa.id}
                className="dropdown-item text-warning notificacao-vencimento"
                onClick={() => {
                  setOpenNotificacoes(false);
                  history.push(`/app/calibracao-prensa/${prensa.id}`);
                }}
              >
                <BuildIcon fontSize="small" className="me-2" />
                <span className="notificacao-vencimento-conteudo">
                  <span className="notificacao-vencimento-titulo">{prensa.nome}</span>
                  <span className="notificacao-vencimento-detalhe">
                    Vencimento: {formatarDataVencimento(prensa.vencimento)} ·{" "}
                    {montarStatusVencimento(prensa.diasRestantes)}
                  </span>
                </span>
              </button>
            ))}
          </>
        )}

        {locacoesAVencer.length > 0 && (
          <>
            <h6 className="dropdown-header text-warning">
              Locações próximas do término
            </h6>

            {locacoesAVencer.map((locacao) => (
              <button
                key={locacao.id}
                className="dropdown-item notificacao-vencimento"
                onClick={() => {
                  setOpenNotificacoes(false);
                  history.push(`/app/locacao-equipamentos/${locacao.id}`);
                }}
              >
                <BuildIcon fontSize="small" className="me-2" />
                <span className="notificacao-vencimento-conteudo">
                  <span className="notificacao-vencimento-titulo">
                    {locacao.codigo || "Locação"} · {locacao.clienteNome || "Cliente não informado"}
                  </span>
                  <span className="notificacao-vencimento-detalhe">
                    Término: {formatarDataVencimento(locacao.vencimento)} · {montarStatusVencimento(locacao.diasRestantes)} · Renovar ou encerrar
                  </span>
                </span>
              </button>
            ))}
          </>
        )}

        {medicoesParaHoje.length > 0 && (
          <>
            <h6 className="dropdown-header text-warning">
              Medições para hoje
            </h6>

            {medicoesParaHoje.map((medicao) => (
              <button
                key={medicao.id}
                className="dropdown-item text-warning notificacao-vencimento"
                onClick={() => {
                  setOpenNotificacoes(false);
                  history.push(`/app/medicoes/${medicao.id}`);
                }}
              >
                <PlaylistAddCheckIcon fontSize="small" className="me-2" />
                <span className="notificacao-vencimento-conteudo">
                  <span className="notificacao-vencimento-titulo">
                    {medicao.codigo || "Medição"} · {medicao.obra || medicao.cliente || "Obra não informada"}
                  </span>
                  <span className="notificacao-vencimento-detalhe" style={{ color: "inherit" }}>
                    Prazo: {formatarDataVencimento(medicao.vencimento)} · {montarStatusVencimento(medicao.diasRestantes)}
                  </span>
                </span>
              </button>
            ))}
          </>
        )}

        {medicoesAtrasadas.length > 0 && (
          <>
            <h6 className="dropdown-header text-danger">
              Medições atrasadas
            </h6>

            {medicoesAtrasadas.map((medicao) => (
              <button
                key={medicao.id}
                className="dropdown-item text-danger notificacao-vencimento"
                onClick={() => {
                  setOpenNotificacoes(false);
                  history.push(`/app/medicoes/${medicao.id}`);
                }}
              >
                <PlaylistAddCheckIcon fontSize="small" className="me-2" />
                <span className="notificacao-vencimento-conteudo">
                  <span className="notificacao-vencimento-titulo">
                    {medicao.codigo || "Medição"} · {medicao.obra || medicao.cliente || "Obra não informada"}
                  </span>
                  <span className="notificacao-vencimento-detalhe" style={{ color: "inherit" }}>
                    Prazo: {formatarDataVencimento(medicao.vencimento)} · {montarStatusVencimento(medicao.diasRestantes)}
                  </span>
                </span>
              </button>
            ))}
          </>
        )}
      </div>
    )}
  </li>
)}

           

           
          </ul>
          
          {usuario && (
            <div
              className="usuario-logado"
              title={`Tipo: ${tipoUsuarioLabel}\nE-mail: ${usuario.email || "Não informado"}`}
              style={{ cursor: "help" }}
              aria-label={`Usuário ${tipoUsuarioLabel}, e-mail ${usuario.email || "não informado"}`}
            >
              <span className="texto-usuario-logado">
                {usuario.displayName || usuario.email}
              </span>
              <img
                src="/Images/perfil.png"
                className="imgUsuario"
                alt="Usuário"
              />
            </div>
          )}
        </div>
      </div>
      {openLogoutModal && (
  <div
    className="modal fade show d-block"
    tabIndex="-1"
    style={{ zIndex: 2000 }}
  >
    <div className="modal-dialog modal-dialog-centered">
      <div
        className="modal-content"
        style={{ pointerEvents: "auto" }}
      >
        <div className="modal-header">
          <h5 className="modal-title">Confirmar saída</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setOpenLogoutModal(false)}
          />
        </div>

        <div className="modal-body">
          <p>Deseja realmente sair do sistema?</p>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={() => setOpenLogoutModal(false)}
          >
            Não
          </button>

          <button
            className="btn btn-danger"
            onClick={confirmarLogout}
          >
            Sim, sair
          </button>
        </div>
      </div>
    </div>

    </div>

)}


    </nav>
    <div className="navbar-page-spacer" aria-hidden="true" />
    <Dialog
      open={openAlertaCaixinha}
      onClose={fecharAlertaCaixinha}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, color: "error.main" }}>
        <AccountBalanceWalletIcon />
        Saldo do caixinha
      </DialogTitle>
      <DialogContent>
        <Typography>
          O saldo atual é de {saldoDinheiroCaixinha?.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL"
          })}. É necessário fazer o depósito do dinheiro.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={fecharAlertaCaixinha}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={openParabens && Boolean(aniversarianteLogado)}
      onClose={() => setOpenParabens(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
        Feliz aniversário, {aniversarianteLogado?.nome?.split(" ")[0] || "colaborador"}! 🎉
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: "center", py: 1 }}>
          <Typography sx={{ fontSize: 64, lineHeight: 1, mb: 2 }}>🎂</Typography>
          <Typography color="text.secondary">
            Desejamos a você um dia muito especial, com saúde, alegria e muitas conquistas!
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
        <Button variant="contained" onClick={() => setOpenParabens(false)}>
          Obrigado!
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export default Navbar;

